import {
  DiscoveryApi,
  FetchApi,
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  FieldExtensionComponentProps,
  createScaffolderFieldExtension,
} from '@backstage/plugin-scaffolder-react';
import {
  FormControl,
  FormHelperText,
  Input,
  InputLabel,
} from '@material-ui/core';
import type { FieldValidation } from '@rjsf/utils';
import { useState } from 'react';

const INVISIBLE_ORG = 'invisible-tech';

// Module-level state is intentional: the scaffolder validation function is called outside
// the React lifecycle and cannot access component state or refs. Only one instance of this
// field is expected per form.
let orgMembershipValid: boolean | null = null;

type OrgMembershipCheckOptions = {
  org: string;
  username: string;
};

export async function isUserMemberOfOrg(
  fetchApi: FetchApi,
  discoveryApi: DiscoveryApi,
  { org, username }: OrgMembershipCheckOptions,
): Promise<boolean> {
  if (!org || !username) return false;
  const baseUrl = await discoveryApi.getBaseUrl('github');
  const res = await fetchApi.fetch(`${baseUrl}/orgs/${org}/members/${username}`, {
    credentials: 'include',
  });
  const body = await res.json();
  return !!body.member;
}

export const GithubUsernameField = ({
  onChange,
  required,
  formData,
}: FieldExtensionComponentProps<string>) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [helperText, setHelperText] = useState<string>('Owner of the repo (admin)');
  const [err, setErr] = useState<string | undefined>(undefined);
  const [isChecking, setIsChecking] = useState(false);

  const handleChange = (input: string) => {
    setErr(undefined);
    setHelperText('Owner of the repo (admin)');
    if (input.trim() !== formData?.trim()) {
      orgMembershipValid = null;
    }
    onChange(input);
  };

  const handleBlur = async () => {
    const username = (formData ?? '').trim();
    if (!username) {
      orgMembershipValid = null;
      return;
    }

    setIsChecking(true);
    try {
      const isMember = await isUserMemberOfOrg(fetchApi, discoveryApi, { org: INVISIBLE_ORG, username });
      orgMembershipValid = isMember;
      if (isMember) {
        setErr(undefined);
        setHelperText(`Looks good! ${username} is a member`);
      } else {
        setErr(`This user is not a member of ${INVISIBLE_ORG}.\nOnly members of Invisible org are allowed`);
        setHelperText('');
      }
    } catch (e: unknown) {
      orgMembershipValid = null;
      setErr('Could not verify org membership');
      setHelperText('Try again or contact an admin');
    } finally {
      setIsChecking(false);
    }
  };

  const showError = orgMembershipValid === false;

  return (
    <FormControl margin="normal" required={required} error={showError}>
      <InputLabel htmlFor="ghUsername">Github Username</InputLabel>
      <Input
        id="ghUsername"
        aria-describedby="entityName"
        value={formData ?? ''}
        onChange={e => handleChange(e.target?.value)}
        onBlur={handleBlur}
        disabled={isChecking}
      />
      <FormHelperText id="entityName">
        {isChecking ? 'Checking membership…' : err ?? helperText}
      </FormHelperText>
    </FormControl>
  );
};

export const GithubUsernameFieldValidation = (value: string, validation: FieldValidation) => {
  if (!value?.trim()) {
    validation.addError('GitHub username is required');
    return;
  }
  // Basic GitHub username pattern (simple, not exhaustive)
  if (!/^[a-z\d](?:[a-z\d-]{0,38})$/i.test(value)) {
    validation.addError('Invalid GitHub username format');
    return;
  }
  if (orgMembershipValid === null) {
    validation.addError('Checking...');
  } else if (!orgMembershipValid) {
    validation.addError('Github Username: invalid');
  }
};

export const GHUsernameFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'GHUsernameField',
    component: GithubUsernameField,
    validation: GithubUsernameFieldValidation,
  }),
);
